/**
 * The admin registration list and removal verbs' wire contracts (plan
 * 2026-09-09-1309, U10 — R6, R7, R8, R14, R15, R16, R41, R43; KTD8).
 *
 *   GET  /v1/ticketed-events/:eventPublicId/admissions
 *   POST /v1/ticketed-events/:eventPublicId/admissions/{cancel,void,deactivate,reactivate,reinstate}
 *   POST /v1/ticketed-events/:eventPublicId/admissions/{edit,transfer,resend}
 *   POST /v1/ticketed-events/:eventPublicId/admissions/{comp,box-office,walk-up}   (U6)
 *   GET  /v1/ticketed-events/:eventPublicId/admissions/:admissionPublicId/history   (U18)
 *
 * Naming (KTD1): the schemas say `admission`; every string an admin reads says
 * "registration".
 *
 * Every verb takes a SET of ids and answers one outcome per distinct id, in
 * request order (R16). A refusal — already in that state, a transition the
 * registration cannot make, an id not on this event, a full tier — is an
 * outcome on that id, never an error for the call, so the dashboard renders
 * each row's result inline.
 */

import { z } from 'zod'
import {
  TicketedEventAdmissionSchema,
  TicketedEventAdmissionStatusSchema
} from '../../schemas/entities/ticketed-event-admission'

/** Same bound as the service: one call holds event and tier locks for every id it touches. */
export const MAX_ADMISSIONS_PER_VERB_CALL = 500

// ============================================================================
// List
// ============================================================================

/**
 * One registration as the admin list shows it: the client-visible seat plus the
 * admin-only state an organizer acts on. EVERY status is listed — a cancelled
 * or voided registration stays readable with its reason, so a removal is never
 * unauditable the moment it happens. The dashboard groups rows by `order.id`.
 */
export const AdminEventAdmissionSchema = TicketedEventAdmissionSchema.extend({
  /** The reason recorded for the CURRENT status; null when none was given. */
  statusReason: z.string().nullable(),
  /** True when an admin carried this registration past the tier's capacity (R41). */
  overCapacity: z.boolean(),
  /** Free-text seat placement ("Table 4, seat 2"), independent of the table (U15 — R36). */
  placement: z.string().nullable(),
  /** What this registration cost, frozen at issue; null on a free one. */
  priceCents: z.number().int().nullable(),
  /** The part of the price the school counts as a contribution (R37); null when none is recorded. */
  contributionCents: z.number().int().nullable(),
  /** The table this registration is assigned to (U15 — R35); null when it has none. */
  seatingGroup: z
    .object({
      id: z.string(),
      name: z.string()
    })
    .nullable(),
  /** The order the registration came from; null for one an admin issued directly. */
  order: z
    .object({
      id: z.string(),
      referenceCode: z.string()
    })
    .nullable()
})
export type AdminEventAdmission = z.infer<typeof AdminEventAdmissionSchema>

/** Not paged, like the event's other admin lists: one event's registrations are bounded by its capacity. */
export const ListEventAdmissionsResponseSchema = z.object({
  data: z.array(AdminEventAdmissionSchema)
})
export type ListEventAdmissionsResponse = z.infer<typeof ListEventAdmissionsResponseSchema>

// ============================================================================
// Verbs
// ============================================================================

const AdmissionReasonSchema = z.string().trim().min(1).max(1000)

const AdmissionVerbBaseShape = {
  admissionPublicIds: z.array(z.string().min(1)).min(1).max(MAX_ADMISSIONS_PER_VERB_CALL),
  /** Stored as the registration's reason and in its history. */
  reason: AdmissionReasonSchema.optional()
}

/**
 * What happens to the seat a cancel or void releases (R15). Omitted means
 * `return`. `hold` offers the seat to the event's waitlist first (U13 — R39);
 * on an event with no waitlist, or with nobody waiting who fits, it behaves as
 * `return`.
 */
export const AdmissionRemovalDispositionSchema = z.enum(['return', 'hold', 'none'])
export type AdmissionRemovalDisposition = z.infer<typeof AdmissionRemovalDispositionSchema>

/** Cancel and void. Strict, so a misspelled field is a 400 rather than silently ignored. */
export const RemoveAdmissionsRequestSchema = z
  .object({
    ...AdmissionVerbBaseShape,
    disposition: AdmissionRemovalDispositionSchema.optional()
  })
  .strict()
export type RemoveAdmissionsRequest = z.infer<typeof RemoveAdmissionsRequestSchema>

/** Deactivate and reactivate. No disposition: neither releases the seat. */
export const ToggleAdmissionsRequestSchema = z.object(AdmissionVerbBaseShape).strict()
export type ToggleAdmissionsRequest = z.infer<typeof ToggleAdmissionsRequestSchema>

/** Reinstate (R43). */
export const ReinstateAdmissionsRequestSchema = z
  .object({
    ...AdmissionVerbBaseShape,
    /**
     * Reinstate past the tier's capacity (R41). Without it, a full tier answers
     * `capacity_exhausted` and the dashboard asks for one as a second, explicit step.
     */
    overCapacityReason: AdmissionReasonSchema.optional()
  })
  .strict()
export type ReinstateAdmissionsRequest = z.infer<typeof ReinstateAdmissionsRequestSchema>

export const AdmissionVerbOutcomeKindSchema = z.enum([
  'cancelled',
  'voided',
  'deactivated',
  'reactivated',
  'reinstated',
  /** Already in the verb's target state. Nothing changed (R14). */
  'already_in_state',
  /** The registration's state does not allow this verb, e.g. reinstating a voided one. */
  'invalid_transition',
  /** Not a registration on this event at this school. */
  'not_found',
  /** Reinstate only: the tier is full and no over-capacity reason was given. */
  'capacity_exhausted'
])
export type AdmissionVerbOutcomeKind = z.infer<typeof AdmissionVerbOutcomeKindSchema>

export const AdmissionVerbOutcomeSchema = z.object({
  admissionPublicId: z.string(),
  outcome: AdmissionVerbOutcomeKindSchema,
  /** The registration's status after the call; null when it was not found. */
  status: TicketedEventAdmissionStatusSchema.nullable(),
  /** Reinstate only: true when it came back past capacity on the given reason. */
  overCapacity: z.boolean().optional(),
  /** Cancel or void of a paid registration: the refund now owed. */
  refundRequestPublicId: z.string().nullable().optional()
})
export type AdmissionVerbOutcome = z.infer<typeof AdmissionVerbOutcomeSchema>

/** Every verb's response: one outcome per distinct requested id, in request order. */
export const AdmissionVerbResponseSchema = z.object({
  outcomes: z.array(AdmissionVerbOutcomeSchema)
})
export type AdmissionVerbResponse = z.infer<typeof AdmissionVerbResponseSchema>

// ============================================================================
// Check-in (U8 — R21, R23)
// ============================================================================
//
//   POST /v1/ticketed-events/:eventPublicId/admissions/check-in

/** Check registrations in (`present: true`) or out. An explicit intent, never a toggle. */
export const AdmissionsCheckInRequestSchema = z
  .object({
    admissionPublicIds: AdmissionVerbBaseShape.admissionPublicIds,
    present: z.boolean()
  })
  .strict()
export type AdmissionsCheckInRequest = z.infer<typeof AdmissionsCheckInRequestSchema>

export const AdmissionCheckInOutcomeKindSchema = z.enum([
  'checked_in',
  'checked_out',
  /** Already in the requested state. A registration already in keeps its original time. */
  'already_in_state',
  /** Refusals: only an active registration can be checked in or out. */
  'cancelled',
  'voided',
  'deactivated',
  'not_found'
])
export type AdmissionCheckInOutcomeKind = z.infer<typeof AdmissionCheckInOutcomeKindSchema>

export const AdmissionsCheckInResponseSchema = z.object({
  outcomes: z.array(
    z.object({
      admissionPublicId: z.string(),
      outcome: AdmissionCheckInOutcomeKindSchema,
      /** The registration's status after the call; null when it was not found. */
      status: TicketedEventAdmissionStatusSchema.nullable(),
      checkedInAt: z.string().nullable()
    })
  )
})
export type AdmissionsCheckInResponse = z.infer<typeof AdmissionsCheckInResponseSchema>

// ============================================================================
// Edit holder, change ticket type, resend ticket (U7 — R11, R12, R13, R44)
// ============================================================================
//
//   POST /v1/ticketed-events/:eventPublicId/admissions/edit
//   POST /v1/ticketed-events/:eventPublicId/admissions/transfer
//   POST /v1/ticketed-events/:eventPublicId/admissions/resend

/** A blank string clears the field; the service trims, and lowercases the email. */
const HolderNameFieldSchema = z.string().trim().max(255)
const HolderEmailFieldSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(320)
  .refine(value => value === '' || z.email().safeParse(value).success, { message: 'Enter a valid email address' })

/**
 * Edit one registration: who holds it, its seat placement and its contribution
 * (U7, U15 — R11, R36, R37). Only the fields sent are changed; `""` clears a
 * text field and `contributionCents: null` clears the contribution, which may
 * not exceed the registration's price.
 */
export const EditAdmissionHolderRequestSchema = z
  .object({
    admissionPublicId: z.string().min(1),
    holder: z
      .object({
        firstName: HolderNameFieldSchema.optional(),
        lastName: HolderNameFieldSchema.optional(),
        email: HolderEmailFieldSchema.optional()
      })
      .strict()
      .refine(h => h.firstName !== undefined || h.lastName !== undefined || h.email !== undefined, {
        message: 'Provide at least one holder field'
      })
      .optional(),
    placement: z.string().trim().max(255).optional(),
    contributionCents: z.number().int().min(0).nullable().optional()
  })
  .strict()
  .refine(b => b.holder !== undefined || b.placement !== undefined || b.contributionCents !== undefined, {
    message: 'Provide at least one field to change'
  })
export type EditAdmissionHolderRequest = z.infer<typeof EditAdmissionHolderRequestSchema>

export const EditAdmissionHolderResponseSchema = z.object({
  /**
   * `holder_edited`; `already_in_state` when nothing differed (no history row);
   * `invalid_transition` for a voided registration, which is never edited.
   */
  outcome: z.enum(['holder_edited', 'already_in_state', 'invalid_transition']),
  /** The registration as it stands after the call. */
  admission: AdminEventAdmissionSchema
})
export type EditAdmissionHolderResponse = z.infer<typeof EditAdmissionHolderResponseSchema>

/**
 * Move registrations to another ticket type on the same event, keeping each
 * registration's id, order, price and history (R13). Hidden ticket types are
 * allowed targets.
 */
export const TransferAdmissionsRequestSchema = z
  .object({
    admissionPublicIds: AdmissionVerbBaseShape.admissionPublicIds,
    /** The target ticket type's public id. */
    targetTicketTypeId: z.string().min(1),
    /**
     * Move past the target's capacity (R41). Without it, a full target answers
     * `capacity_exhausted` and the dashboard asks for one as a second step.
     */
    overCapacityReason: AdmissionReasonSchema.optional()
  })
  .strict()
export type TransferAdmissionsRequest = z.infer<typeof TransferAdmissionsRequestSchema>

export const TransferAdmissionOutcomeKindSchema = z.enum([
  'transferred',
  /** Already on the target ticket type. Nothing changed. */
  'already_in_state',
  /** Only active and deactivated registrations move. */
  'invalid_transition',
  'not_found',
  /** The target is full and no over-capacity reason was given. Nothing moved. */
  'capacity_exhausted'
])
export type TransferAdmissionOutcomeKind = z.infer<typeof TransferAdmissionOutcomeKindSchema>

export const TransferAdmissionsResponseSchema = z.object({
  outcomes: z.array(
    z.object({
      admissionPublicId: z.string(),
      outcome: TransferAdmissionOutcomeKindSchema,
      /** The registration's status after the call; null when it was not found. */
      status: TicketedEventAdmissionStatusSchema.nullable(),
      /** True when it moved past the target's capacity on the given reason. */
      overCapacity: z.boolean().optional()
    })
  )
})
export type TransferAdmissionsResponse = z.infer<typeof TransferAdmissionsResponseSchema>

/**
 * Bound on one resend. Each id is a provider call made while the admin waits,
 * so this is far below the state verbs' bound; the dashboard sends one order's
 * registrations at a time.
 */
export const MAX_ADMISSIONS_PER_RESEND = 50

export const ResendAdmissionTicketsRequestSchema = z
  .object({
    admissionPublicIds: z.array(z.string().min(1)).min(1).max(MAX_ADMISSIONS_PER_RESEND)
  })
  .strict()
export type ResendAdmissionTicketsRequest = z.infer<typeof ResendAdmissionTicketsRequestSchema>

export const ResendAdmissionOutcomeKindSchema = z.enum([
  /** The email provider accepted the send. Not proof of delivery. */
  'sent',
  /** Neither the registration nor its order has an email address. */
  'no_recipient',
  /** The school has no ticket email set up yet. Nothing was sent for any id. */
  'not_configured',
  /** Only active registrations are sent. */
  'invalid_transition',
  'not_found',
  /** The email provider refused or timed out. Safe to try again. */
  'send_failed'
])
export type ResendAdmissionOutcomeKind = z.infer<typeof ResendAdmissionOutcomeKindSchema>

export const ResendAdmissionTicketsResponseSchema = z.object({
  outcomes: z.array(
    z.object({
      admissionPublicId: z.string(),
      outcome: ResendAdmissionOutcomeKindSchema
    })
  )
})
export type ResendAdmissionTicketsResponse = z.infer<typeof ResendAdmissionTicketsResponseSchema>

// ============================================================================
// Admin-added registrations (U6)
// ============================================================================

/** Seats one admin request may add. Every seat shares the ticket type and the holder. */
export const MAX_ADMIN_MINT_QUANTITY = 20

/**
 * The client's key for one add-registration dialog. Seat `i` is stored under
 * `${idempotencyKey}:${i}`, so the key stops at 150 characters to leave room for
 * the suffix inside the column's 191.
 */
export const ADMIN_MINT_IDEMPOTENCY_KEY_MAX_LENGTH = 150

const HolderNameSchema = z.string().trim().min(1).max(255)

/** Lowercased and trimmed: the holder lookup seeks `holder_email = ?` and never lowercases. */
const HolderEmailSchema = z.string().trim().toLowerCase().email().max(320)

const AdminMintBaseShape = {
  /** The ticket type's public id. Must be a type on this event. */
  ticketTypeId: z.string().min(1),
  /** Seats to add, all with the same ticket type and holder. Omitted means 1. */
  quantity: z.number().int().min(1).max(MAX_ADMIN_MINT_QUANTITY).optional(),
  /** Generated once per dialog open. Resending it returns the same registrations and adds nothing. */
  idempotencyKey: z.string().min(1).max(ADMIN_MINT_IDEMPOTENCY_KEY_MAX_LENGTH),
  /**
   * Add past the ticket type's capacity (R41). Without it a full type answers
   * 409 `CAPACITY_EXHAUSTED` and the dashboard asks for one as a second step.
   */
  overCapacityReason: AdmissionReasonSchema.optional()
}

/** A box-office sale or a walk-up names the person; the email stays optional. */
const NamedHolderSchema = z
  .object({ firstName: HolderNameSchema, lastName: HolderNameSchema, email: HolderEmailSchema.optional() })
  .strict()

/** `POST …/admissions/comp`. A comp may leave the holder unassigned. */
export const CompMintRequestSchema = z
  .object({
    ...AdminMintBaseShape,
    holder: z
      .object({
        firstName: HolderNameSchema.optional(),
        lastName: HolderNameSchema.optional(),
        email: HolderEmailSchema.optional()
      })
      .strict()
      .optional()
  })
  .strict()
export type CompMintRequest = z.infer<typeof CompMintRequestSchema>

/**
 * `POST …/admissions/box-office`. `priceCents` defaults to the ticket type's
 * price; `contributionCents` is the part the school counts as a contribution
 * and may not exceed the price.
 */
export const BoxOfficeMintRequestSchema = z
  .object({
    ...AdminMintBaseShape,
    holder: NamedHolderSchema,
    priceCents: z.number().int().min(0).optional(),
    contributionCents: z.number().int().min(0).optional()
  })
  .strict()
export type BoxOfficeMintRequest = z.infer<typeof BoxOfficeMintRequestSchema>

/** `POST …/admissions/walk-up`. Charged at the ticket type's price; hidden types are refused. */
export const WalkUpMintRequestSchema = z.object({ ...AdminMintBaseShape, holder: NamedHolderSchema }).strict()
export type WalkUpMintRequest = z.infer<typeof WalkUpMintRequestSchema>

export const AdminMintResponseSchema = z.object({
  /** The registrations the request added (or, on a replay, already added), in seat order. */
  admissions: z.array(AdminEventAdmissionSchema),
  /** How many of them went past capacity on the given reason. */
  overCapacityCount: z.number().int().min(0)
})
export type AdminMintResponse = z.infer<typeof AdminMintResponseSchema>

/**
 * `error.details` on the 409 `CAPACITY_EXHAUSTED` a request without an
 * over-capacity reason gets. `available` is how many of the `requested` seats
 * would have fit; nothing was added.
 */
export const AdminMintCapacityExhaustedDetailsSchema = z.object({
  requested: z.number().int(),
  available: z.number().int()
})
export type AdminMintCapacityExhaustedDetails = z.infer<typeof AdminMintCapacityExhaustedDetailsSchema>

// ============================================================================
// Registration history (U18 — R30; KTD7)
// ============================================================================
//
//   GET /v1/ticketed-events/:eventPublicId/admissions/:admissionPublicId/history?cursor=&limit=

/** Rows per history page when `limit` is omitted, and the most one page may ask for. */
export const ADMISSION_HISTORY_DEFAULT_LIMIT = 50
export const ADMISSION_HISTORY_MAX_LIMIT = 100

/** Who made a change. `name` is display copy the server has already resolved. */
export const AdmissionHistoryActorSchema = z.object({
  type: z.enum(['admin', 'door_token', 'buyer', 'service_token', 'system']),
  name: z.string()
})
export type AdmissionHistoryActor = z.infer<typeof AdmissionHistoryActorSchema>

/**
 * The SAFE part of a history row's recorded detail: only these fields ever leave
 * the server, each null when the change did not record it. Holder edits report
 * WHICH fields changed, never the old or new values, and no email or form
 * answer appears anywhere in a row.
 */
export const AdmissionHistoryDetailsSchema = z.object({
  /** What happened to the seat a cancel or void released. `hold` arrives with waitlist offers (U13). */
  disposition: z.enum(['return', 'hold', 'none']).nullable(),
  /** The ticket type a registration moved off. */
  fromTicketType: z.string().nullable(),
  /** The ticket type it moved onto, or the one it was added on. */
  toTicketType: z.string().nullable(),
  /** True when the change carried the registration past capacity (R41). */
  overCapacity: z.boolean().nullable(),
  /** Holder fields an edit changed, by name only. */
  changedHolderFields: z.array(z.enum(['firstName', 'lastName', 'email'])).nullable(),
  /** How many registrations an admin added in the same request as this one. */
  quantity: z.number().int().nullable()
})
export type AdmissionHistoryDetails = z.infer<typeof AdmissionHistoryDetailsSchema>

export const AdmissionHistoryRowSchema = z.object({
  /** When it happened, ISO 8601. */
  at: z.string(),
  /** The full `SystemEventLog.eventType`, e.g. `ticketed_events.admission.cancelled`. */
  eventType: z.string(),
  /**
   * The event type's last segment, e.g. `cancelled`. A plain string rather than
   * an enum so a verb added later reaches an older dashboard as a row it can
   * still show.
   */
  verb: z.string(),
  actor: AdmissionHistoryActorSchema,
  /** The reason the actor gave; null when none was given. */
  reason: z.string().nullable(),
  details: AdmissionHistoryDetailsSchema
})
export type AdmissionHistoryRow = z.infer<typeof AdmissionHistoryRowSchema>

/** Newest first. Pass `nextCursor` back as `cursor` for the next page; null on the last page. */
export const AdmissionHistoryResponseSchema = z.object({
  data: z.array(AdmissionHistoryRowSchema),
  nextCursor: z.string().nullable()
})
export type AdmissionHistoryResponse = z.infer<typeof AdmissionHistoryResponseSchema>
